/**
 * Delivery-speed check for the Sober cart drawer.
 *
 * One request. The service at data-api resolves everything — fanning out to the
 * fulfilment centres, checking FGGN stock for the cart's SKUs, and deciding the
 * promise. Earlier versions did the lane checks here in the browser, which meant four
 * requests, the FC list sitting in page source, and the decision split across two
 * codebases. It also could not read stock: that needs an Eshopbox token whose scopes
 * include write:orders and write:payouts, which can never live in a theme.
 *
 * Trade-off of consolidating: the service is now required. If it is unreachable the
 * widget shows its error state rather than falling back to a pincode-only answer.
 *
 * A same-day / next-day verdict is published to localStorage under
 * `breeze_cart_attributes`, which Breeze maps onto the order's Additional details,
 * where Shopify Flow reads it to apply the delivery tag. Cart attributes are written
 * alongside for debugging and in case checkout ever moves back to Shopify's own.
 */

const CACHE_KEY = 'sbr_delivery_check_v1'
const CACHE_TTL = 30 * 60 * 1000
const PINCODE_RE = /^[1-9][0-9]{5}$/

/* Breeze rebuilds note_attributes from scratch, so cart attributes alone never reach
 * the order. This localStorage key is the channel Breeze actually reads. */
const BREEZE_KEY = 'breeze_cart_attributes'
const ATTR_KEYS = ['delivery_speed', 'delivery_pincode', 'delivery_etd', 'delivery_fc']

/* Title-case keys an earlier version wrote. Cart attributes persist on the cart, so
 * these linger as duplicates on the order until existing carts age out. */
const LEGACY_ATTR_KEYS = ['Delivery Speed', 'Delivery Pincode', 'Delivery ETA', 'Delivery From FC']

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }

/** Copy and the terms URL come from theme settings, so escape before innerHTML. */
function esc(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ESCAPES[c])
}

/** "2026-08-29" -> "Fri, 29 Aug". Built from local date parts rather than
 *  Date(string), which would parse as UTC and can shift the day. */
function formatIsoDate(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return ''
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
}

class SbrDeliveryCheck extends HTMLElement {
  connectedCallback() {
    this.api = (this.dataset.api || '').trim()

    this.wrap = this.querySelector('[data-sbr-dc-form]')
    this.input = this.querySelector('[data-sbr-dc-input]')
    this.button = this.querySelector('[data-sbr-dc-button]')
    this.result = this.querySelector('[data-sbr-dc-result]')

    if (!this.wrap || !this.input || !this.button || !this.result) return

    if (!this.api) {
      // Not configured in the theme editor — hide rather than offer a control that
      // cannot answer.
      this.hidden = true
      return
    }

    this.abortController = new AbortController()
    const signal = this.abortController.signal

    // No form element here on purpose: header-cart-drawer.js binds CartForm to the
    // first form inside the drawer, so one here would leave line items unrendered.
    this.button.addEventListener('click', this.handleSubmit.bind(this), { signal })
    this.input.addEventListener('keydown', this.handleKeydown.bind(this), { signal })
    this.input.addEventListener('input', this.handleInput.bind(this), { signal })

    this.restore()
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort()
  }

  /* ---------------------------------------------------------------- state */

  restore() {
    let cached = null
    try {
      cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null')
    } catch (e) {
      cached = null
    }
    if (!cached || !cached.pincode) return

    this.input.value = cached.pincode
    if (cached.result && Date.now() - cached.ts < CACHE_TTL) this.render(cached.result)
  }

  persist(pincode, result) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ pincode, result, ts: Date.now() }))
    } catch (e) {
      /* private mode / quota — caching is an optimisation, not a requirement */
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Enter') return
    event.preventDefault()
    this.handleSubmit(event)
  }

  handleInput() {
    // Clear a stale verdict as soon as they start editing the pincode.
    if (this.result.dataset.state && this.result.dataset.state !== 'error') {
      this.result.dataset.state = ''
      this.result.innerHTML = ''
    }
  }

  /* ------------------------------------------------------------- fetching */

  async handleSubmit(event) {
    event.preventDefault()
    const pincode = (this.input.value || '').trim()

    if (!PINCODE_RE.test(pincode)) {
      this.render({ state: 'invalid' })
      this.input.focus()
      return
    }

    this.setLoading(true)
    try {
      const result = await this.check(pincode)
      this.render(result)
      this.persist(pincode, result)
      await this.saveVerdict(pincode, result)
    } catch (e) {
      this.render({ state: 'error' })
    } finally {
      this.setLoading(false)
    }
  }

  async check(pincode) {
    // Read the cart live: the drawer is updated by AJAX, so anything rendered at page
    // load is stale the moment an item is added or removed.
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
    const cartRes = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      signal: this.abortController.signal
    })
    if (!cartRes.ok) throw new Error('cart read failed')

    const items = ((await cartRes.json()) || {}).items || []
    const skus = [...new Set(items.map((i) => (i.sku || '').trim()).filter(Boolean))]
    const hasUnidentifiedLine = items.some((i) => !(i.sku || '').trim())

    const res = await fetch(this.api, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pincode, skus, hasUnidentifiedLine }),
      signal: this.abortController.signal
    })
    if (!res.ok) throw new Error(`delivery check failed: ${res.status}`)

    const json = await res.json()
    return {
      state: json.speed,
      date: json.date,
      label: formatIsoDate(json.date),
      etd: json.etd || json.date,
      fc: json.fc || '',
      estimated: json.estimated
    }
  }

  /** Publish a fast verdict to both channels, or clear them when it no longer applies.
   *  Clearing matters: without it, someone who checks a next-day pincode and then a
   *  slow one would carry the stale verdict onto their order. */
  async saveVerdict(pincode, result) {
    const qualifies = result.state === 'same-day' || result.state === 'next-day'
    const fields = qualifies
      ? {
          delivery_speed: result.state === 'same-day' ? 'Same day' : 'Next day',
          delivery_pincode: pincode,
          delivery_etd: result.etd,
          delivery_fc: result.fc
        }
      : {}

    this.writeBreezeAttributes(fields)
    await this.writeCartAttributes(fields)
  }

  /** Merge rather than overwrite — consent, UTM and brand scripts share this key. */
  writeBreezeAttributes(fields) {
    let store = {}
    try {
      const parsed = JSON.parse(localStorage.getItem(BREEZE_KEY) || 'null')
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) store = parsed
    } catch (e) {
      store = {}
    }

    ATTR_KEYS.concat(LEGACY_ATTR_KEYS).forEach((key) => delete store[key])
    Object.keys(fields).forEach((key) => {
      store[key] = String(fields[key])
    })

    try {
      localStorage.setItem(BREEZE_KEY, JSON.stringify(store))
    } catch (e) {
      /* private mode / quota */
    }
  }

  async writeCartAttributes(fields) {
    const attributes = {}
    ATTR_KEYS.forEach((key) => {
      attributes[key] = fields[key] === undefined ? '' : String(fields[key])
    })
    // An empty string removes a cart attribute
    LEGACY_ATTR_KEYS.forEach((key) => {
      attributes[key] = ''
    })

    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      })
    } catch (e) {
      /* the customer-facing answer is already correct; tagging is best-effort */
    }
  }

  /* -------------------------------------------------------------- render */

  setLoading(isLoading) {
    this.button.classList.toggle('is-loading', isLoading)
    this.button.disabled = isLoading
    this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false')
    if (isLoading) this.result.dataset.state = ''
  }

  render(result) {
    const copy = {
      'same-day': { icon: 'tick', text: this.dataset.copySameDay },
      'next-day': { icon: 'tick', text: this.dataset.copyNextDay },
      standard: { icon: 'dot', text: this.dataset.copyStandard },
      invalid: { icon: 'cross', text: this.dataset.copyInvalid },
      error: { icon: 'cross', text: this.dataset.copyError }
    }[result.state]

    if (!copy) return

    const text = esc((copy.text || '').replace('[date]', result.label || ''))

    // Terms belong beside a delivery promise, not a validation or network error.
    const url = this.dataset.tncUrl
    const label = this.dataset.tncLabel
    const wantsTerms = ['same-day', 'next-day', 'standard'].indexOf(result.state) !== -1
    const terms =
      wantsTerms && url && label
        ? `<a class="sbr-dc__tnc" href="${esc(url)}" target="_blank" rel="noopener">${esc(label)}</a>`
        : ''

    this.result.dataset.state = result.state
    this.result.innerHTML =
      `<span class="sbr-dc__icon" data-icon="${copy.icon}" aria-hidden="true"></span>` +
      `<span class="sbr-dc__message">${text}</span>` +
      terms
  }
}

if (!customElements.get('sbr-delivery-check')) {
  customElements.define('sbr-delivery-check', SbrDeliveryCheck)
}
